import { mapIvimType } from '_store/ivim/ivimsTypes';
import {
  ivimFilterDateSelector,
  ivimFilterServiceCategoryCodeSelector,
  ivimFilterServiceSubCategoryCodeSelector,
  ivimFilterSourceSelector,
  ivimFilterStatusSelector,
  ivimFilterUpdatedFromSelector,
  ivimFilterUpdatedToSelector,
  ivimsAllData,
  loadingCSVSelector,
} from '_store/ivim/selectors';
import { mapPictogramCategoryId, useIvimsRequest } from '_store/ivim/utils';
import { selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { formatDateForCSV } from '_store/utils';
import { useLocalization } from '@fluent/react';
import { faFileArrowDown, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import isEqual from 'lodash/isEqual';
import Papa from 'papaparse';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

interface ExportCSVProps {
  show: boolean;
  filename: string;
}

export const ExportCSV = ({ show = false, filename }: ExportCSVProps) => {
  const { l10n } = useLocalization();
  const prevTransformedIvimsRef = useRef<
    {
      [x: string]: string | number;
    }[]
  >();

  const [buttonClicked, setButtonClicked] = useState(false);

  const selectedRoadSegmentIds = useSelector(selectedRoadSegmentIdsSelector);
  const ivimsStatus = useSelector(ivimFilterStatusSelector);
  const ivimsDate = useSelector(ivimFilterDateSelector);
  const ivimsUpdatedFrom = useSelector(ivimFilterUpdatedFromSelector);
  const ivimsUpdatedTo = useSelector(ivimFilterUpdatedToSelector);
  const ivimsServiceCategoryCode = useSelector(ivimFilterServiceCategoryCodeSelector);
  const ivimsServiceSubCategoryCode = useSelector(ivimFilterServiceSubCategoryCodeSelector);
  const ivimsSourceFilter = useSelector(ivimFilterSourceSelector);

  const dispatchIvimsRequest = useIvimsRequest({ getAllData: true });
  const ivimsToExport = useSelector(ivimsAllData);
  const ivimsCSVLoading = useSelector(loadingCSVSelector);

  const downloadLabel = l10n.getString('forms-event-download-csv');

  const filenameWithDate = useMemo(() => {
    const date = new Date();
    const formattedDate = `${date.getFullYear()}${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}_${date
      .getHours()
      .toString()
      .padStart(2, '0')}${date.getMinutes().toString().padStart(2, '0')}${date
      .getSeconds()
      .toString()
      .padStart(2, '0')}`;
    return `${filename}_${formattedDate}.csv`;
  }, [filename]);

  const getPictogramCode = (ivim) => {
    const categoryId = mapPictogramCategoryId(
      ivim.serviceCategoryCode,
      ivim.serviceSubCategoryCode,
    );
    return `${categoryId} ${ivim.pictogramNature}${ivim.pictogramSerialNumber}`;
  };

  const localizedStrings = useMemo(
    () => ({
      id: l10n.getString('forms-ivim-id'),
      countryCode: l10n.getString('forms-ivim-sp-country-code'),
      providerId: l10n.getString('forms-ivim-sp-country-id'),
      type: l10n.getString('forms-ivim-type'),
      validFrom: l10n.getString('forms-ivim-valid-from'),
      validTo: l10n.getString('forms-ivim-valid-to'),
      referencePoint: l10n.getString('forms-ivim-reference-point'),
      pictogramCode: l10n.getString('forms-ivim-pictogram-code'),
      serviceCategoryCode: l10n.getString('forms-ivim-ser-cat-cod'),
      serviceSubCategoryCode: l10n.getString('forms-ivim-ser-sub-cat-cod'),
      source: l10n.getString('forms-ivim-source'),
    }),
    [l10n],
  );

  const transformedIvims = useMemo(() => ivimsToExport.map((ivim) => ({
      ID: ivim.id,
      [localizedStrings.id]: ivim.id,
      [localizedStrings.countryCode]: ivim.countryCode || '-',
      [localizedStrings.providerId]: ivim.providerIdentifier || '-',
      [localizedStrings.type]: `${mapIvimType(ivim.iviType, l10n)}`,
      [localizedStrings.validFrom]: formatDateForCSV(ivim.starts),
      [localizedStrings.validTo]: formatDateForCSV(ivim.ends),
      [localizedStrings.referencePoint]: `${ivim.referencePoint.coordinates[1]}, ${ivim.referencePoint.coordinates[0]}`,
      [localizedStrings.pictogramCode]: getPictogramCode(ivim),
      [localizedStrings.serviceCategoryCode]: ivim.serviceCategoryCode,
      [localizedStrings.serviceSubCategoryCode]: ivim.serviceSubCategoryCode,
      [localizedStrings.source]:
        ivim.source === 'datexii' ? l10n.getString('forms-event-source-datexii') : ivim.source,
      'Connected DENMs': ivim.metadata.connectedDenms
        ? ivim.metadata.connectedDenms.map((denm) => denm.emerasId).join(', ')
        : '-',
      'Free Text': ivim.metadata.freeText
        ? ivim.metadata.freeText.map((text) => `${text.value} (${text.language})`).join('; ')
        : '-',
      'Extra Text': ivim.metadata.extraText
        ? ivim.metadata.extraText.map((text) => `${text.value} (${text.language})`).join('; ')
        : '-',
    })), [ivimsToExport, localizedStrings, l10n]);

  const downloadCSV = () => {
    const csv = Papa.unparse(transformedIvims, { delimiter: ';' });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filenameWithDate;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (buttonClicked && !isEqual(prevTransformedIvimsRef.current, transformedIvims)) {
      downloadCSV();
      setButtonClicked(false);
    }
    prevTransformedIvimsRef.current = transformedIvims;
  }, [transformedIvims]);

  const handleClick = () => {
    dispatchIvimsRequest(selectedRoadSegmentIds, {
      status: ivimsStatus,
      date: ivimsDate,
      updatedFrom: ivimsUpdatedFrom,
      updatedTo: ivimsUpdatedTo,
      serviceCategoryCode: ivimsServiceCategoryCode,
      serviceSubCategoryCode: ivimsServiceSubCategoryCode,
      sources: ivimsSourceFilter,
    });
    setButtonClicked(true);
  };

  if (!show) {
    return null;
  }

  return (
    <button onClick={handleClick}>
      {downloadLabel}
      {ivimsCSVLoading ? (
        <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: 10 }} />
      ) : (
        <FontAwesomeIcon icon={faFileArrowDown} size="lg" style={{ marginLeft: 10 }} />
      )}
    </button>
  );
};
